import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CONSTANTS } from '@pk/powerkioskutils';
import { RouterService } from 'src/app/core/router.service';

import { BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import _ from 'lodash';
import { BillReaderAuthService } from 'src/app/bill-reader/services/bill-reader-auth.service';
import { FaqSearchModalComponent } from 'src/app/faq/modals/faq-search-modal/faq-search-modal.component';
import { SecurityService } from 'src/app/security/security.service';
import { AgentDiscountProgram, Breadcrumb, Company, Menu, User } from 'src/app/shared/models';
import { environment } from '../../../environments/environment';
import { GraphqlService } from '../../graphql/graphql.service';
import { Agent, Attachment } from '../../shared/models';
import { AppStateService } from '../app-state.service';

@Component({
	selector: 'pk-broker-navbar',
	templateUrl: './navbar.component.html',
	styleUrls: ['./navbar.component.scss'],
	standalone: false,
})
export class NavbarComponent implements OnInit {
	@Output() userUpdated: EventEmitter<User> = new EventEmitter();
	@Output() agentUpdated: EventEmitter<Agent> = new EventEmitter();
	@Output() useDefaultUpdated: EventEmitter<boolean> = new EventEmitter();
	@Output() navbarLogoUpdated: EventEmitter<Attachment> = new EventEmitter();
	@Output() footerLogoUpdated: EventEmitter<Attachment> = new EventEmitter();
	@Output() energyPortalLogoUpdated: EventEmitter<Attachment> = new EventEmitter();

	@Input() isDirect = false;
	@Input() isBillReader = false;
	@Input() isPopupWindow = false;
	@Input() isEnergyPortal = false;

	public useDefault: boolean;
	public user: User;
	public breadcrumbs = [];
	public searchForm: FormGroup<{ search: FormControl<string> }>;
	public environment = environment;
	public loading = true;
	public window = window;
	public useSupportSystem = true;
	public supportEmail = '';
	public attachment: Attachment;
	public menus: Menu[];
	public unreadUpdatedCount: number = null;
	public agentDiscountPrograms: AgentDiscountProgram[];
	public route: any;
	public showFinancialProfile: boolean;
	public showPricingAnalytics: boolean;
	public isFinancialProfileCollapsed = true;
	public isMenuCollapsed = true;
	public isFromToggle = false;

	public defaultNavbarLogoAttachment: Attachment;
	public defaultFooterLogoAttachment: Attachment;
	public defaultEnergyPortalLogoAttachment: Attachment;
	public envName = `broker${environment.name}`;

	constructor(
		private securityService: SecurityService,
		private graphqlService: GraphqlService,
		private modalService: BsModalService,
		private toastrService: ToastrService,
		private appStateService: AppStateService,
		private routerService: RouterService,
		private fb: FormBuilder,
		private billReaderAuthService: BillReaderAuthService,
		private faIconLibrary: FaIconLibrary,
	) {
		this.appStateService.onSetBreadcrumbs.subscribe((breadcrumbs: Breadcrumb[]) => {
			Promise.resolve().then(() => {
				this.breadcrumbs = breadcrumbs;
			});
		});
		this.appStateService.onUserUpdated.subscribe(async () => {
			this.loading = true;
			await this.getNavbarData();
			this.loading = false;
		});
		this.appStateService.onAgentUpdated.subscribe(async (agentId: string) => {
			this.loading = true;
			await this.getNavbarAgentData(agentId);
			this.loading = false;
		});
		this.appStateService.onSetDefault.subscribe(async (companyId: string) => {
			this.loading = true;
			await this.getNavbarCompanyData(companyId);
			this.loading = false;
		});
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	@HostListener('document:click', ['$event']) public onClick(event: any): void {
		return this.collapseMenu();
	}

	get loggedInUser() {
		return this.securityService.authFields?.loggedInUser;
	}

	// Enterprise users have no agent, so their branding comes off the enterprise. Both display logos already fall back
	// to the company default server-side, so defaultNavbarLogoAttachment only matters when there is neither.
	get navbarLogoAttachment(): Attachment {
		return this.user?.agent?.darkDisplayLogoAttachment ?? this.user?.enterprise?.darkDisplayLogoAttachment ?? this.defaultNavbarLogoAttachment;
	}

	async ngOnInit(): Promise<void> {
		if (!this.isDirect && this.loggedInUser) {
			await this.getNavbarData();
			this.buildSearchForm();
		}

		this.loading = false;
	}

	private async getNavbarCompanyData(companyId: string): Promise<void> {
		try {
			const result = await this.graphqlService.getNavbarCompanyData(companyId);
			this.setDefaultLogo(result.data.company);
		} catch (e) {
			this.toastrService.warning(
				'There was a problem loading the page. We have been notified and are working to fix the issue. ' + 'Please check back again in 30 minutes.',
				'Warning',
			);
		}
	}

	private async getNavbarAgentData(agentId: string): Promise<void> {
		this.loading = true;
		try {
			const result = await this.graphqlService.getNavbarAgentData(agentId);
			this.user = new User();
			this.user.companyId = result.data.agent.companyId;
			this.user.isSaas = result.data.agent.company.isSaas;
			this.user.phone = result.data.agent.phone;
			this.user.email = new Agent(result.data.agent).getLogin().email;
			this.user.agent = result.data.agent;
			this.user.company = this.user.agent.company;
			this.setDefaultLogo(this.user.agent.company);

			this.userUpdated.emit(this.user);
			this.agentUpdated.emit(this.user.agent);
		} catch (e) {
			this.toastrService.warning(
				'There was a problem loading the page. We have been notified and are working to fix the issue. ' + 'Please check back again in 30 minutes.',
				'Warning',
			);
		}
		this.loading = false;
	}

	private async getNavbarData(): Promise<void> {
		const result = await this.graphqlService.getNavbarData();

		this.user = _.cloneDeep(this.loggedInUser);
		if (this.user.agent) {
			this.agentDiscountPrograms = result.data.agentDiscountPrograms.message.map(r => new AgentDiscountProgram(r));
			this.user.phone = this.user.phone;
			this.user.email = this.user.email;
		}

		if (!this.user.company) {
			await this.getNavbarCompanyData(CONSTANTS.companies.powerKiosk);
		} else {
			this.setDefaultLogo(this.user.company);
		}

		this.showFinancialProfile =
			this.user.hasCommission &&
			this.user.agent &&
			!this.user.enterpriseId &&
			this.user.agent.isActive &&
			(this.user.agent.parent
				? this.user.agent.showFinancialProfile && this.user.agent.parent.showFinancialProfile
				: this.user.agent.showFinancialProfile);

		this.supportEmail = '';
		if (this.user.agent && this.user.agent.parent) {
			this.supportEmail = this.user.agent.parent.supportEmail;
		}

		this.userUpdated.emit(this.user);
		this.agentUpdated.emit(this.user.agent);
	}

	public logout(): void {
		if (this.isBillReader) {
			this.billReaderAuthService.logout();
		} else {
			this.securityService.logout();
		}
	}

	get search() {
		return this.searchForm.get('search');
	}
	get isImpersonating() {
		return this.securityService.isImpersonating;
	}
	get isDemoEnergyPortal() {
		return !!environment?.demo && this.isEnergyPortal;
	}
	get canShowDemoEnergyPortal() {
		return !!environment?.demo && this.user?.agent?.showEnergyPortal && !this.user?.isSaas;
	}

	get showMenu(): boolean {
		return !this.user || this.user.isAdmin || this.user.isOwner || (this.user.agent?.isActive && !this.user.enterpriseId);
	}

	private isAtLeast4Characters(): boolean {
		if (!this.search.value) {
			return false;
		}
		this.search.setValue(this.search.value.replace(/\s{2,}/g, ' ').trim());
		this.search.updateValueAndValidity();
		return this.search.value.length >= 4;
	}

	private setDefaultLogo(company: Company): void {
		this.defaultNavbarLogoAttachment = company.defaultNavbarLogoAttachment;
		this.defaultFooterLogoAttachment = company.defaultFooterLogoAttachment;
		this.defaultEnergyPortalLogoAttachment = company.defaultEnergyPortalLogoAttachment;

		this.footerLogoUpdated.emit(this.defaultFooterLogoAttachment);
		this.energyPortalLogoUpdated.emit(this.defaultEnergyPortalLogoAttachment);
	}

	public async onSubmit(): Promise<void> {
		const lengthIsValid = this.isAtLeast4Characters();
		if (this.searchForm.valid && lengthIsValid) {
			await this.routerService.navigate(['/search'], { queryParams: { query: this.searchForm.value.search } }).then(() => {
				this.searchForm.reset();
			});
		} else {
			if (this.search.errors || !lengthIsValid) {
				this.toastrService.warning('Must be at least 4 characters to search!', 'Search Result');
			}
		}
	}

	public openFaqSearch(): void {
		this.modalService.show(FaqSearchModalComponent, {
			class: 'pk-modal modal-dialog-slideout',
			ignoreBackdropClick: true,
		});
	}

	private buildSearchForm(): void {
		this.searchForm = this.fb.group({
			search: this.fb.control('', [Validators.required]),
		});
	}

	public async navigateHome(): Promise<void> {
		if (!this.isDirect) {
			if (window.location.pathname === '/contract') {
				window.location.reload();
			} else {
				await this.routerService.navigate([environment.contracts.home]);
			}
		}
	}

	public navigate(route: string): boolean {
		if (window.location.pathname === '/contract' && route === '/contract') {
			window.location.reload();
			return false;
		} else {
			return true;
		}
	}

	get showBranding(): boolean {
		return (
			!this.loading &&
			(!this.isDirect || !!this.defaultNavbarLogoAttachment || (!!this.user?.agent && (this.isDirect || !!this.defaultNavbarLogoAttachment)))
		);
	}

	public toggleFinancialProfileProgram(toggle: boolean): void {
		this.isFinancialProfileCollapsed = toggle;
	}

	public toggleMenu(): void {
		this.isFromToggle = true;
		this.isMenuCollapsed = !this.isMenuCollapsed;
	}

	public collapseMenu(): void {
		if (!this.isFromToggle) {
			this.isMenuCollapsed = true;
		}

		this.isFromToggle = false;
	}

	public runMethod(method: string): void {
		this[method]();
	}

	public isIconAvailable(prefix: string, iconName: string): boolean {
		return !!this.faIconLibrary.getIconDefinition(prefix as any, iconName as any);
	}
}
